import { Chevron, Minus, Pin, Plus, Status } from 'app/assets/icons'
import { media } from 'app/assets/styles'
import { Buttons, OpeningStatus, ServicesList } from 'app/common/restaurant'
import { CaptionText, Text, SmallText, fonts } from 'app/common/typography'
import React from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import { COLOR } from 'app-v2/design-tokens'
import { eventsDefinitions } from 'shared/store/metadata'
import styled from 'styled-components'
import type { RestaurantShortDTO } from 'shared/types'
import type { Coordinates } from 'shared/store/store-locator'
import { restaurantServices, restaurantStatus } from 'shared/utils'
import { colors } from 'app-v2/design-systems/theme/colors'

const { red, text } = oldColors
const { roboto } = fonts

type Props = {
    restaurant: RestaurantShortDTO,
    active: boolean,
    collapsible?: boolean,
    onSelectRestaurant?: any,
    origin?: Coordinates,
    minified?: boolean,
    intl: IntlShape,
    clickAndCollect?: boolean
}

const RestaurantComponent = ({
    restaurant,
    active,
    collapsible,
    onSelectRestaurant,
    origin,
    clickAndCollect,
    minified = false,
    intl,
    ...otherProps
}: Props) => {
    const {
        id,
        status,
        driveStatus,
        name,
        addressFull,
        services,
        distance,
        pageUrl,
        lat,
        lng,
        deliverooUrl,
        uberEatsUrl,
        cacStatus,
        openingCacHour,
        closingCacHour,
        openingHour,
        closingHour
    } = restaurant

    if (!!clickAndCollect && status === restaurantStatus.SOON) {
        return null
    }

    let metaRestaurantSheet = { ...eventsDefinitions['RESTAURANT_SHEET'] }
    metaRestaurantSheet.event_label = name
    let metaRestaurantDirections = {
        ...eventsDefinitions['RESTAURANT_DIRECTIONS'],
        event_label: name
    }
    metaRestaurantSheet.event_label = name

    const hasClickAndCollect = Boolean(
        services.find(
            element => element === restaurantServices.CLICK_AND_COLLECT
        )
    )
    const clickAndCollectClickable =
        hasClickAndCollect &&
        (cacStatus === restaurantStatus.OPENED ||
            cacStatus === restaurantStatus.IMMINENT_CLOSING)
    return (
        <RestaurantContainer
            minified={minified}
            onClick={e =>
                onSelectRestaurant && onSelectRestaurant(e, restaurant)
            }
            disabled={clickAndCollect && !clickAndCollectClickable}
            clickAndCollect={clickAndCollect}
            {...otherProps}>
            <Pin color={active ? colors.fieryRed : COLOR.PRIMARY} />
            <Infos clickAndCollect={clickAndCollect}>
                <div>
                    <Title>
                        <RestaurantName clickAndCollect={clickAndCollect}>
                            {name}
                        </RestaurantName>
                        {!minified && (
                            <Distance clickAndCollect={clickAndCollect}>
                                <SmallText>{distance}</SmallText>
                                {collapsible && (active ? <Minus /> : <Plus />)}
                            </Distance>
                        )}
                    </Title>
                    <InfoContainer>
                        <div>
                            <Address clickAndCollect={clickAndCollect}>
                                {addressFull}
                            </Address>
                            {!clickAndCollect || hasClickAndCollect ? (
                                <OpeningStatus
                                    id={id}
                                    status={
                                        clickAndCollect && cacStatus
                                            ? cacStatus
                                            : status
                                    }
                                    driveStatus={driveStatus}
                                    openingHour={
                                        clickAndCollect && cacStatus
                                            ? openingCacHour
                                            : openingHour
                                    }
                                    closingHour={
                                        clickAndCollect && cacStatus
                                            ? closingCacHour
                                            : closingHour
                                    }
                                    clickAndCollect={
                                        clickAndCollect && cacStatus
                                    }
                                />
                            ) : (
                                <CCUnavailableServiceContainer
                                    clickAndCollect={clickAndCollect}>
                                    <Status color={red} />
                                    <FormattedMessage id="restaurant.cac.closed">
                                        {(text: string) => (
                                            <StyledCaptionText color={red}>
                                                {text}
                                            </StyledCaptionText>
                                        )}
                                    </FormattedMessage>
                                </CCUnavailableServiceContainer>
                            )}
                            {!minified && !clickAndCollect && (
                                <Services>
                                    {services && services.length > 0 && (
                                        <ServicesList
                                            services={services}
                                            color={COLOR.PRIMARY}
                                        />
                                    )}
                                </Services>
                            )}
                        </div>
                        {clickAndCollect && clickAndCollectClickable && (
                            <Chevron color={COLOR.PRIMARY} size={30} />
                        )}
                    </InfoContainer>
                </div>
                {!minified && active && (
                    <RestaurantButtons>
                        <Buttons
                            origin={origin}
                            destination={{ lat, lng }}
                            metaDirection={metaRestaurantDirections}
                            services={services}
                            deliverooUrl={deliverooUrl}
                            uberEatsUrl={uberEatsUrl}
                            restaurantPath={pageUrl}
                            restaurant={restaurant}
                            cacStatus={cacStatus}
                            className="restaurantDetail"
                        />
                    </RestaurantButtons>
                )}
            </Infos>
        </RestaurantContainer>
    )
}

const RestaurantContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 92%;
    margin: ${props => !props.minified && '10px 0'};
    padding: 10px;
    ${props =>
        props.clickAndCollect &&
        `
        background-color: white;
        border-radius: 10px;
        margin: 15px 1%;
    `}
    ${props =>
        props.disabled
            ? `cursor: default;`
            : `
        cursor: pointer;
    `}
    ${media.medium`
        width: 96%;
    `};
`

const Infos = styled.div`
    width: 100%;
    margin-left: 20px;
    ${props =>
        props.clickAndCollect &&
        `
        align-items: center;
        margin-left: 0;
    `};
`

const Title = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    svg {
        display: none;
        ${media.medium`
           display: block;
           margin-left: 30px;
        `};
    }
`

const RestaurantName = styled(Text)`
    ${props =>
        props.clickAndCollect &&
        `font-family: ${roboto};
        font-weight: 700;
    `};
`

const Address = styled(CaptionText)`
    display: block;
    color: ${text};
    margin: 2px 0 5px;

    ${media.small`
        margin: 5px 0 5px;
    `}
    ${props =>
        props.clickAndCollect &&
        `
        margin: 0;
    `}
`

const InfoContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const Services = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
`

const CCUnavailableServiceContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    ${props => !props.clickAndCollect && `padding-left: 10px`};
`

const StyledCaptionText = styled(CaptionText)`
    margin-left: 5px;
`

const Distance = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    ${props =>
        props.clickAndCollect &&
        `
        margin-left: -50px;
    `}
`

const RestaurantButtons = styled.div`
    display: none;
    ${media.medium`
        margin-top: 10px;
        display: block;
    `};
`

export const Restaurant = injectIntl(RestaurantComponent)
